import {useMemo} from "react";
import {useEntityTypes} from "../api.ts";
import {
    buildEstimateCatalogs,
    estimateQueuedItem,
    LocationLike,
    QueueEstimateResult,
    QueueLikeItem
} from "./estimate.ts";
import useCivLevelMap from "./useCivLevelMap.ts";

interface UseProductionEstimateResult {
    estimateQueuedItem: (
        item: QueueLikeItem,
        holderEntityType: string,
        location: LocationLike,
        startMinAt: number,
        startMaxAt: number,
        managementSkill?: number | null
    ) => QueueEstimateResult;
    isPending: boolean;
    isError: boolean;
    isReady: boolean;
}

const MISSING_DATA_RESULT: QueueEstimateResult = {
    estimate: {
        status: 'missing_data',
        reason: 'Estimator catalogs not loaded yet',
    },
    nextStartMinAt: 0,
    nextStartMaxAt: 0,
};

export function useProductionEstimate(): UseProductionEstimateResult {
    const {
        ships,
        vehicles,
        items,
        droids,
        isPending: entitiesPending,
        isError: entitiesError,
    } = useEntityTypes(['ships', 'vehicles', 'items', 'droids']);

    const civ = useCivLevelMap();

    const context = useMemo(() => {
        if (entitiesPending || civ.isPending || entitiesError || civ.isError) {
            return null;
        }

        return {
            catalogs: buildEstimateCatalogs({
                ships: ships as any[],
                vehicles: vehicles as any[],
                items: items as any[],
                droids: droids as any[],
            }),
            civLookup: civ.data
        };
    }, [ships, vehicles, items, droids, entitiesPending, entitiesError, civ.data, civ.isPending, civ.isError]);

    const estimateFn = useMemo(() => {
        return (
            item: QueueLikeItem,
            holderEntityType: string,
            location: LocationLike,
            startMinAt: number,
            startMaxAt: number,
            managementSkill?: number | null
        ): QueueEstimateResult => {
            if (!context) {
                return {
                    ...MISSING_DATA_RESULT,
                    nextStartMinAt: startMinAt,
                    nextStartMaxAt: startMaxAt,
                };
            }
            return estimateQueuedItem(item, holderEntityType, location, context.catalogs, context.civLookup, startMinAt, startMaxAt, managementSkill);
        };
    }, [context]);

    return {
        estimateQueuedItem: estimateFn,
        isPending: entitiesPending || civ.isPending,
        isError: entitiesError || civ.isError,
        isReady: !!context,
    };
}
